// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { ArrowOutwardRounded } from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import { CostFilter, CostSortProperty } from '@/costs/api/get-costs';
import { CostTable } from '@/costs/components/cost-table';
import { useCosts } from '@/costs/hooks/use-costs';
import { Box, LinearProgress, Paper, Stack, Typography } from '@mui/material';

interface Props {
  sort: Sort<CostSortProperty>;
  filter?: CostFilter;
  onSort: (property: CostSortProperty) => void;
  onPageChange: (page: number) => void;
}

/**
 * A view component that displays a table of costs with loading states and pagination.
 */
export function CostTableView(props: Props) {
  const { costs, totalCount, isFetching } = useCosts({
    filter: props.filter,
  });

  const downloadURL = new URL('/v1/export/costs', import.meta.env.VITE_API_URL);
  const pageNumber = props.filter?.pageNumber ?? 1;
  const pageSize = props.filter?.pageSize ?? 50;

  if (costs.length === 0) {
    return (
      <EmptyState title="No costs found">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography color="text.secondary">
            Start tracking costs by adding your first cost entry
          </Typography>
          <ArrowOutwardRounded fontSize="small" color="action" />
        </Stack>
      </EmptyState>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Paper variant="outlined" component={Box}>
        {isFetching && <LinearProgress />}
        <CostTable sort={props.sort} onSort={props.onSort} costs={costs} />
      </Paper>
      <TablePaginationFooter
        pagination={{
          page: pageNumber,
          pageSize,
          onPageChange: props.onPageChange,
        }}
        totalCount={totalCount}
        entityOptions={{ singular: 'cost', includeCount: false }}
        downloadURL={downloadURL}
        downloadLabel="Export cost data"
      />
    </Box>
  );
}
